import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Filter, FilterService, ViewOption } from '@brafton/angular-framework-library';

@Component({
  selector: 'shared-filter-bar',
  templateUrl: './filter-bar.component.html',
  styleUrls: ['./filter-bar.component.scss'],
  host: { class: 'shared-filter-bar' },
})
export class SharedFilterBarComponent implements OnInit, OnDestroy {
  // A placeholder for all subscripton so we can unsubscribe all of them in the ondestroy Methode.
  subscriptions = new Subscription();
  @Input() resultsCount!: number;
  @Input() campaigns = false;
  @Input() contentTypes = false;
  @Input() dateRange = false;
  @Input() departments = false;
  @Input() industrySectors = false;
  @Input() months = false;
  @Input() organizations = false;
  @Input() owners = false;
  @Input() ownersByTeam = false;
  @Input() paceOffset = false;
  @Input() productClasses = false;
  @Input() projectStatuses = false;
  @Input() sows = false;
  @Input() sowTypes = false;
  @Input() statuses = false;
  @Input() tags = false;
  @Input() teams = false;
  @Input() templates = false;
  @Input() text = false;
  @Input() definitionId = false;
  @Input() units = false;
  @Input() viewOptions!: ViewOption[];
  @Input() versionOptions!: ViewOption[];
  @Input() navOptions!: NavOption[];
  @Input() debounceFilterChanges = true;

  @Input() teamId!: number;
  @Input() feedId?: number;
  @Input() parentTagId?: string;

  @Input() defaultFilters!: Filter[];
  @Input() readOnlyFilters!: Filter[];

  @Input() filteredTags!: Filter[];
  @Output() filteredTagsChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredDateRange!: Filter[];
  @Output() filteredDateRangeChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredContentTypes!: Filter[];
  @Output() filteredContentTypesChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredCampaigns!: Filter[];
  @Output() filteredCampaignsChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredStatuses!: Filter[];
  @Output() filteredStatusesChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredSows!: Filter[];
  @Output() filteredSowsChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredOwners!: Filter[];
  @Output() filteredOwnersChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredOwnersByTeam!: Filter[];
  @Output() filteredOwnersByTeamChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredMonths!: Filter[];
  @Output() filteredMonthsChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredTeams!: Filter[];
  @Output() filteredTeamsChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredTemplates!: Filter[];
  @Output() filteredTemplatesChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredDepartments!: Filter[];
  @Output() filteredDepartmentsChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredOrganizations!: Filter[];
  @Output() filteredOrganizationsChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredUnits!: Filter[];
  @Output() filteredUnitsChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredPaceOffset!: Filter[];
  @Output() filteredPaceOffsetChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredSowTypes!: Filter[];
  @Output() filteredSowTypesChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredText!: Filter[];
  @Output() filteredTextChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredDefinitionId!: Filter[];
  @Output() filteredDefinitionIdChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredProductClasses!: Filter[];
  @Output() filteredProductClassesChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() filteredIndustrySectors!: Filter[];
  @Output() filteredIndustrySectorsChange: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  @Input() selectedViewOptions!: ViewOption[];
  @Output() selectedViewOptionsChange: EventEmitter<ViewOption[]> = new EventEmitter<ViewOption[]>();

  @Input() selectedVersionOptions!: ViewOption[];
  @Output() selectedVersionOptionsChange: EventEmitter<ViewOption[]> = new EventEmitter<ViewOption[]>();

  @Output() refreshData: EventEmitter<Filter[]> = new EventEmitter<Filter[]>();

  public filterOption = '';
  public filterCount = 0;
  public viewOption?: ViewOption;
  public versionOption?: ViewOption;

  constructor(private filterService: FilterService) {}

  ngOnInit() {
    this.initFilterTracking();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private initFilterTracking() {
    this.subscriptions.add(
      this.filterService
        .registerFilters(this.filtersArray, this.defaultFilters)
        .pipe(debounceTime(this.debounceFilterChanges === true ? 2000 : 0))
        .subscribe((filters: any) => {
          this.filterCount = filters.length;
          const isEveryDefaultFilterUsed = this.defaultFilters?.every((df) =>
            filters.some((f: Filter) => f.type === df.type && f.value === df.value),
          );
          this.filterCount =
            isEveryDefaultFilterUsed === true ? filters.length - this.defaultFilters.length : filters.length;

          console.log(`Refreshing ${filters.length} filters`);

          if (this.tags) {
            this.filteredTags = filters.filter((f: Filter) => f.type === 'tag');
            this.filteredTagsChange.emit(this.filteredTags);
          }

          if (this.filteredDateRange) {
            this.filteredDateRange = filters.filter((f: Filter) => f.type === 'dateRange');
            this.filteredDateRangeChange.emit(this.filteredDateRange);
          }

          if (this.contentTypes) {
            this.filteredContentTypes = filters.filter((f: Filter) => f.type === 'contentType');
            this.filteredContentTypesChange.emit(this.filteredContentTypes);
          }

          if (this.campaigns) {
            this.filteredCampaigns = filters.filter((f: Filter) => f.type === 'campaign');
            this.filteredCampaignsChange.emit(this.filteredCampaigns);
          }

          if (this.statuses) {
            this.filteredStatuses = filters.filter((f: Filter) => f.type === 'status');
            this.filteredStatusesChange.emit(this.filteredStatuses);
          }

          if (this.projectStatuses) {
            this.filteredStatuses = filters.filter((f: Filter) => f.type === 'status');
            this.filteredStatusesChange.emit(this.filteredStatuses);
          }

          if (this.sows) {
            this.filteredSows = filters.filter((f: Filter) => f.type === 'sow');
            this.filteredSowsChange.emit(this.filteredSows);
          }

          if (this.owners) {
            this.filteredOwners = filters.filter((f: Filter) => f.type === 'user');
            this.filteredOwnersChange.emit(this.filteredOwners);
          }

          if (this.ownersByTeam) {
            this.filteredOwnersByTeam = filters.filter((f: Filter) => f.type === 'user');
            this.filteredOwnersByTeamChange.emit(this.filteredOwnersByTeam);
          }

          if (this.months) {
            this.filteredMonths = filters.filter((f: Filter) => f.type === 'month');

            this.filteredMonthsChange.emit(this.filteredMonths);
          }

          if (this.teams) {
            this.filteredTeams = filters.filter((f: Filter) => f.type === 'team');
            this.filteredTeamsChange.emit(this.filteredTeams);
          }

          if (this.templates) {
            this.filteredTemplates = filters.filter((f: Filter) => f.type === 'template');
            this.filteredTemplatesChange.emit(this.filteredTemplates);
          }

          if (this.productClasses) {
            this.filteredProductClasses = filters.filter((f: Filter) => f.type === 'product-class');
            this.filteredProductClassesChange.emit(this.filteredProductClasses);
          }

          if (this.departments) {
            this.filteredDepartments = filters.filter((f: Filter) => f.type === 'department');
            this.filteredDepartmentsChange.emit(this.filteredDepartments);
          }

          if (this.organizations) {
            this.filteredOrganizations = filters.filter((f: Filter) => f.type === 'organization');
            this.filteredOrganizationsChange.emit(this.filteredOrganizations);
          }

          if (this.industrySectors) {
            this.filteredIndustrySectors = filters.filter((f: Filter) => f.type === 'industry-sector');
            this.filteredIndustrySectorsChange.emit(this.filteredIndustrySectors);
          }

          if (this.units) {
            this.filteredUnits = filters.filter((f: Filter) => f.type === 'unit');
            this.filteredUnitsChange.emit(this.filteredUnits);
          }

          if (this.paceOffset) {
            this.filteredPaceOffset = filters.filter((f: Filter) => f.type === 'paceOffset');
            this.filteredPaceOffsetChange.emit(this.filteredPaceOffset);
          }

          if (this.sowTypes) {
            this.filteredSowTypes = filters.filter((f: Filter) => f.type === 'sow-type');
            this.filteredSowTypesChange.emit(this.filteredSowTypes);
          }

          if (this.text) {
            this.filteredText = filters.filter((f: Filter) => f.type === 'text');
            this.filteredTextChange.emit(this.filteredText);
          }

          if (this.definitionId) {
            this.filteredDefinitionId = filters.filter((f: Filter) => f.type === 'definition-id');
            this.filteredDefinitionIdChange.emit(this.filteredDefinitionId);
          }

          if (this.viewOptions) {
            this.selectedViewOptions = filters
              .filter((f: Filter) => this.viewOptions.find((v) => v.type === f.type && v.value === f.value)) //  f.type === 'viewoption')
              .map((f: Filter) => this.viewOptions.find((v) => v.type === f.type && v.value === f.value) || f);
            this.selectedViewOptionsChange.emit(this.selectedViewOptions);
          }

          if (this.versionOptions) {
            this.selectedVersionOptions = filters
              .filter((f: Filter) => this.versionOptions.find((v) => v.type === f.type && v.value === f.value)) //  f.type === 'viewoption')
              .map((f: Filter) => this.versionOptions.find((v) => v.type === f.type && v.value === f.value) || f);
            this.selectedVersionOptionsChange.emit(this.selectedVersionOptions);
          }

          this.refreshData.emit(filters);
        }),
    );
  }

  textChanged(text: any) {
    if (!text || text == '') {
      this.filteredText = [];
    } else {
      const filter = { value: text, displayName: 'Search', type: 'text' } as Filter;
      this.filteredText = [filter];
    }
    this.filterService.updateFilter('text', this.filteredText);
  }

  definitionIdChanged(definitionId: any) {
    if (!definitionId || definitionId == '') {
      this.filteredDefinitionId = [];
    } else {
      const filter = { value: definitionId, displayName: 'definition-id', type: 'text' } as Filter;
      this.filteredDefinitionId = [filter];
    }
    this.filterService.updateFilter('definition-id', this.filteredDefinitionId);
  }

  public get hasFilters(): boolean {
    return (
      this.tags ||
      this.campaigns ||
      this.contentTypes ||
      this.sows ||
      this.statuses ||
      this.projectStatuses ||
      this.owners ||
      this.ownersByTeam ||
      this.industrySectors ||
      this.months ||
      this.departments ||
      this.teams ||
      this.organizations ||
      this.templates ||
      this.productClasses ||
      this.units ||
      this.sowTypes ||
      this.text ||
      this.definitionId ||
      this.paceOffset ||
      this.dateRange ||
      this.versionOptions !== undefined ||
      this.viewOptions !== undefined
    );
  }

  public clearAll() {
    this.filterService.clearAll();
  }

  public getUniqueViewOptions(options: ViewOption[]): ViewOption[] {
    const optionTypes = options.reduce(
      (acc: ViewOption[], cur: ViewOption) => (acc.find((o) => o.type === cur.type) ? acc : acc.concat(cur)),
      [],
    );
    return optionTypes;
  }

  public getUniqueVersionOptions(options: ViewOption[]): ViewOption[] {
    const optionTypes = options.reduce(
      (acc: ViewOption[], cur: ViewOption) => (acc.find((o) => o.type === cur.type) ? acc : acc.concat(cur)),
      [],
    );
    return optionTypes;
  }

  public getViewTypeOptions(options: ViewOption[], type: string): ViewOption[] {
    return options.filter((vo) => vo.type === type);
  }

  public filterContentTypeChanged() {
    this.filterService.updateFilter('contentType', this.filteredContentTypes);
  }

  public filterTagChanged() {
    this.filterService.updateFilter('tag', this.filteredTags);
  }

  public filterDateRangeChanged() {
    this.filterService.updateFilter('dateRange', this.filteredDateRange);
  }

  public filterMonthChanged() {
    this.filterService.updateFilter('month', this.filteredMonths);
  }

  public clearChangedDate(filterChanged: 'dateRange' | 'month') {
    if (!filterChanged) return;
    if (filterChanged === 'dateRange') {
      this.filteredMonths = [];
      this.filterService.updateFilter('month', this.filteredMonths);
      setTimeout(() => {
        this.filterDateRangeChanged();
        this.filteredMonthsChange.emit(this.filteredMonths);
      }, 1000);
      return;
    }

    if (filterChanged === 'month') {
      this.filteredDateRange = [];
      this.filterService.updateFilter('dateRange', this.filteredDateRange);
      setTimeout(() => {
        this.filterMonthChanged();
        this.filteredDateRangeChange.emit(this.filteredDateRange);
      }, 1000);
      return;
    }
  }

  public filterCampaignChanged() {
    this.filterService.updateFilter('campaign', this.filteredCampaigns);
  }

  public filterStatusChanged() {
    this.filterService.updateFilter('status', this.filteredStatuses);
  }

  public filterSowChanged() {
    this.filterService.updateFilter('sow', this.filteredSows);
  }

  public filterTemplateChanged() {
    this.filterService.updateFilter('template', this.filteredTemplates);
  }

  public filterProductClassChanged() {
    this.filterService.updateFilter('product-class', this.filteredProductClasses);
  }

  public filterOwnerChanged() {
    this.filterService.updateFilter('user', this.filteredOwners);
  }

  public filterOwnerbyTeamChanged() {
    this.filterService.updateFilter('user', this.filteredOwnersByTeam);
  }

  public filterDepartmentChanged() {
    this.filterService.updateFilter('department', this.filteredDepartments);
  }

  public filterIndustrySectorChanged() {
    this.filterService.updateFilter('industry-sector', this.filteredIndustrySectors);
  }

  public filterTeamChanged() {
    this.filterService.updateFilter('team', this.filteredTeams);
  }

  public filterOrganizationChanged() {
    this.filterService.updateFilter('organization', this.filteredOrganizations);
  }

  public filteredDefinitionIdChanged() {
    this.filterService.updateFilter('definition-id', this.filteredDefinitionId);
  }

  public filterUnitChanged() {
    this.filterService.updateFilter('unit', this.filteredUnits);
  }

  public filterSowTypeChanged() {
    this.filterService.updateFilter('sow-type', this.filteredSowTypes);
  }

  public filteredPaceOffsetChanged() {
    this.filterService.updateFilter('paceOffset', this.filteredPaceOffset);
  }

  public selectedViewOptionsChanged(optionType: string) {
    this.filterService.updateFilter(optionType, this.getViewTypeOptions(this.selectedViewOptions, optionType));
  }

  public selectedVersionOptionsChanged(optionType: string) {
    this.filterService.updateFilter(optionType, this.getViewTypeOptions(this.selectedVersionOptions, optionType));
  }

  public openFilterPanel(name: string) {
    this.filterOption = name;
    this.viewOption = undefined;
  }

  public openOptionPanel(option: ViewOption) {
    this.viewOption = option;
    this.filterOption = '';
  }

  public closePanels() {
    this.filterOption = '';
    this.viewOption = undefined;
  }

  public iconFromFilterType(filter: Filter): string {
    switch (filter?.type) {
      case 'campaign':
        return 'campaign';
      case 'contentType':
        return 'category';
      case 'tag':
        return 'sell';
      case 'status':
        return 'traffic';
      case 'sow':
        return 'receipt';
      case 'month':
        return 'event';
      case 'dateRange':
        return 'event';
      case 'user':
        return 'person';
      case 'team':
        return 'people';
      case 'work':
        return 'timelapse';
      case 'department':
        return 'groups_2';
      case 'organization':
        return 'corporate_fare';
      case 'industry-sector':
        return 'factory';
      case 'template':
        return 'view_timeline';
      case 'product-class':
        return 'glyphs';
      case 'unit':
        return 'star';
      case 'sow-type':
        return 'receipt';
      case 'text':
        return 'search';
      case 'definition-id':
        return 'list';
      case 'paceOffset':
        return 'star';
      default:
        return 'create';
    }
  }

  private get filtersArray() {
    const result = [];
    if (this.tags === true) result.push('tag');
    if (this.dateRange === true) result.push('dateRange');
    if (this.contentTypes === true) result.push('contentType');
    if (this.campaigns === true) result.push('campaign');
    if (this.statuses === true) result.push('status');
    if (this.projectStatuses === true) result.push('status');
    if (this.sows === true) result.push('sow');
    if (this.owners === true) result.push('user');
    if (this.ownersByTeam === true) result.push('user');
    if (this.months === true) result.push('month');
    if (this.teams === true) result.push('team');
    if (this.departments === true) result.push('department');
    if (this.organizations === true) result.push('organization');
    if (this.templates === true) result.push('template');
    if (this.units === true) result.push('unit');
    if (this.sowTypes === true) result.push('sow-type');
    if (this.paceOffset === true) result.push('paceOffset');
    if (this.text === true) result.push('text');
    if (this.definitionId === true) result.push('definition-id');
    if (this.productClasses === true) result.push('product-class');
    if (this.industrySectors === true) result.push('industry-sector');
    if (this.viewOptions !== undefined)
      this.getUniqueViewOptions(this.viewOptions).forEach((vo) => result.push(vo.type));
    if (this.versionOptions !== undefined)
      this.getUniqueVersionOptions(this.versionOptions).forEach((vo) => result.push(vo.type));

    return result;
  }
}

export class NavOption {
  icon?: string;
  caption!: string;
  route!: string;
}
